import React from 'react';
import { MapPin, Zap, Shield, Check, ArrowRight } from 'lucide-react';
import { QualityBadge } from './QualityBadge';
import { calculateQualityScore, getMeasureXLabel } from '../utils/facilityUtils';
import { Link } from 'react-router-dom';

interface FacilityCardProps {
  facility: any;
}

export function FacilityCard({ facility }: FacilityCardProps) {
  const qualityLevel = calculateQualityScore(facility);
  const measureXLabel = getMeasureXLabel(facility.measureX);

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
      <div className="relative h-48">
        <img 
          src={facility.image} 
          alt={facility.name}
          className="w-full h-full object-cover"
        />
        {facility.status === 'coming-soon' && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-blue-500 text-white text-sm font-medium rounded-full">
            Coming Soon
          </div>
        )}
        <div className="absolute top-4 left-4">
          <QualityBadge quality={qualityLevel} showDetails />
        </div>
      </div>
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              {facility.name}
            </h2>
            <div className="flex items-center text-gray-600">
              <MapPin className="w-4 h-4 mr-1" />
              {facility.location}, {facility.country}
            </div>
          </div>
          <div className="space-y-2">
            <span className={`block px-3 py-1 rounded-full text-sm font-medium ${
              facility.securityLevel === 'enterprise' ? 'text-purple-600 bg-purple-50' :
              facility.securityLevel === 'advanced' ? 'text-blue-600 bg-blue-50' :
              'text-green-600 bg-green-50'
            }`}>
              {facility.securityLevel.charAt(0).toUpperCase() + facility.securityLevel.slice(1)} Security
            </span>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${measureXLabel.color} group relative cursor-help`}>
              {measureXLabel.text}
              <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-white rounded-lg shadow-lg border border-gray-100 hidden group-hover:block z-10">
                <p className="text-sm text-gray-600">{measureXLabel.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="space-y-1">
            <p className="text-sm text-gray-500">Capacity</p>
            <p className="font-semibold text-gray-900">{facility.capacity}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-gray-500">Power Source</p>
            <p className="font-semibold text-gray-900">{facility.powerSource}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-gray-500">Power Cost</p>
            <p className="font-semibold text-gray-900">${facility.powerCost}/kWh</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-gray-500">Minimum Order</p>
            <p className="font-semibold text-gray-900">{facility.minOrder} Units</p>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Features</h3>
          <div className="grid grid-cols-2 gap-2">
            {facility.features.map((feature: string, index: number) => (
              <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                <Check className="w-4 h-4 text-green-500" />
                {feature}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${
              facility.uptime > 99.9 ? 'bg-green-500' : 'bg-yellow-500'
            }`} />
            <span className="text-sm text-gray-600">
              {facility.uptime}% Uptime
            </span>
          </div>
          <Link 
            to={`/facilities/${facility.id}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Learn More
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}